import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import SEO from '@/components/feature/SEO';
import Breadcrumb from '@/components/feature/Breadcrumb';
import { siteInfo } from '@/mocks/site';

const FORM_URL = 'https://readdy.ai/api/form/d9ri3asnlsngrm4lmcgg';

export default function About() {
  const { t } = useTranslation();
  const [formStatus, setFormStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [formError, setFormError] = useState('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'instant' as const });
  }, []);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    const formEl = e.currentTarget;
    const formData = new FormData(formEl);

    const honeyValue = (formData.get('company_alt') as string || '').trim();
    if (honeyValue) {
      setFormStatus('success');
      formEl.reset();
      return;
    }
    formData.delete('company_alt');

    setSubmitting(true);
    setFormStatus('idle');
    setFormError('');

    try {
      const params = new URLSearchParams();
      formData.forEach((value, key) => {
        params.append(key, value as string);
      });

      const response = await fetch(FORM_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: params.toString(),
      });

      const responseText = await response.text();
      let parsed: Record<string, unknown> = {};
      try { parsed = JSON.parse(responseText); } catch { /* ignore */ }

      if (response.ok && parsed?.code === 'OK') {
        setFormStatus('success');
        formEl.reset();
      } else {
        const serverMsg = (parsed?.meta as Record<string, string>)?.message
          || (parsed?.message as string)
          || responseText
          || '提交失败';
        if (typeof serverMsg === 'string' && serverMsg.toLowerCase().includes('spam')) {
          setFormStatus('error');
          setFormError('提交失败，请稍后重试。');
        } else {
          setFormStatus('error');
          setFormError(typeof serverMsg === 'string' ? serverMsg : '提交失败，请稍后重试。');
        }
      }
    } catch {
      setFormStatus('error');
      setFormError('网络错误，请检查网络连接后重试。');
    } finally {
      setSubmitting(false);
    }
  };

  const breadcrumbItems = [
    { label: t('breadcrumb_home'), href: '/' },
    { label: t('breadcrumb_about') },
  ];

  const breadcrumbSchema = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: breadcrumbItems.map((item, index) => ({
      '@type': 'ListItem',
      position: index + 1,
      name: item.label,
      item: item.href ? `${siteInfo.url}${item.href}` : undefined,
    })),
  };

  const orgSchema = {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: siteInfo.name,
    url: siteInfo.url,
    description: siteInfo.description,
    email: siteInfo.email,
    telephone: siteInfo.phone,
    address: {
      '@type': 'PostalAddress',
      addressLocality: '北京',
      addressRegion: '海淀区',
      streetAddress: '中关村南大街5号伦理大厦12层',
      addressCountry: 'CN',
    },
    foundingDate: String(siteInfo.foundingYear),
  };

  const statsData = [
    { value: '2,560+', descKey: 'rep_item_1_desc' },
    { value: '380,000+', descKey: 'rep_item_2_desc' },
    { value: '86位', descKey: 'rep_item_3_desc' },
    { value: '120门', descKey: 'rep_item_4_desc' },
    { value: '98%', descKey: 'rep_item_5_desc' },
    { value: '50+', descKey: 'rep_item_6_desc' },
  ];

  const testimonials = [
    { textKey: 'rep_testimonial_1_text', nameKey: 'rep_testimonial_1_name', titleKey: 'rep_testimonial_1_title' },
    { textKey: 'rep_testimonial_2_text', nameKey: 'rep_testimonial_2_name', titleKey: 'rep_testimonial_2_title' },
    { textKey: 'rep_testimonial_3_text', nameKey: 'rep_testimonial_3_name', titleKey: 'rep_testimonial_3_title' },
  ];

  const partners = [
    'rep_partner_1', 'rep_partner_2', 'rep_partner_3',
    'rep_partner_4', 'rep_partner_5', 'rep_partner_6',
  ];

  return (
    <>
      <SEO
        title={t('page_title')}
        description={`${siteInfo.name} - ${t('section_brand_desc')}`}
        keywords="伦理在线关于我们,伦理学平台介绍,伦理教育机构"
        canonicalUrl={`${siteInfo.url}/about`}
        schema={{ ...breadcrumbSchema, ...orgSchema }}
      />

      <main className="pt-16 md:pt-18">
        <div className="w-full px-4 md:px-6 bg-background-50 border-b border-background-200/70">
          <div className="max-w-6xl mx-auto">
            <Breadcrumb items={breadcrumbItems} />

            <div className="pb-6 md:pb-8">
              <h1 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950 mb-3">
                {t('page_title')}
              </h1>
              <p className="text-sm md:text-base text-foreground-600 max-w-2xl">
                {t('section_brand_desc')}
              </p>
            </div>
          </div>
        </div>

        <section className="w-full px-4 md:px-6 py-10 md:py-16 bg-background-50">
          <div className="max-w-6xl mx-auto">
            <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-950 mb-8">
              {t('brand_story_title')}
            </h2>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 md:gap-12">
              <div className="space-y-4 text-sm md:text-base text-foreground-700 leading-relaxed">
                <p>{t('brand_story_p1')}</p>
                <p>{t('brand_story_p2')}</p>
                <p>{t('brand_story_p3')}</p>
              </div>
              <div className="flex flex-col gap-5">
                <div className="p-5 rounded-lg bg-background-100 border border-background-200/70">
                  <div className="flex items-center gap-2 mb-2">
                    <div className="w-8 h-8 flex items-center justify-center rounded-full bg-primary-100 text-primary-600">
                      <i className="ri-flag-line"></i>
                    </div>
                    <h3 className="font-heading font-semibold text-foreground-950">{t('brand_mission')}</h3>
                  </div>
                  <p className="text-sm text-foreground-600">{t('brand_mission_text')}</p>
                </div>
                <div className="p-5 rounded-lg bg-background-100 border border-background-200/70">
                  <div className="flex items-center gap-2 mb-2">
                    <div className="w-8 h-8 flex items-center justify-center rounded-full bg-accent-100 text-accent-600">
                      <i className="ri-eye-line"></i>
                    </div>
                    <h3 className="font-heading font-semibold text-foreground-950">{t('brand_vision')}</h3>
                  </div>
                  <p className="text-sm text-foreground-600">{t('brand_vision_text')}</p>
                </div>
                <div className="p-5 rounded-lg bg-background-100 border border-background-200/70">
                  <div className="flex items-center gap-2 mb-2">
                    <div className="w-8 h-8 flex items-center justify-center rounded-full bg-secondary-100 text-secondary-700">
                      <i className="ri-heart-line"></i>
                    </div>
                    <h3 className="font-heading font-semibold text-foreground-950">{t('brand_values')}</h3>
                  </div>
                  <p className="text-sm text-foreground-600">{t('brand_values_text')}</p>
                </div>
              </div>
            </div>
          </div>
        </section>

        <section className="w-full px-4 md:px-6 py-10 md:py-16 bg-background-100">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-10 md:mb-14">
              <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-950 mb-3">
                {t('section_reputation')}
              </h2>
              <p className="text-sm md:text-base text-foreground-600 max-w-xl mx-auto">
                {t('section_reputation_desc')}
              </p>
            </div>

            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4 md:gap-5 mb-12">
              {statsData.map((stat, index) => (
                <div key={index} className="text-center p-4 md:p-5 rounded-lg bg-background-50 border border-background-200/70">
                  <div className="font-heading text-xl md:text-2xl font-bold text-primary-600 mb-1">
                    {stat.value}
                  </div>
                  <div className="text-xs text-foreground-600">{t(stat.descKey)}</div>
                </div>
              ))}
            </div>

            <div className="mb-12">
              <h3 className="font-heading text-lg font-semibold text-foreground-950 mb-5 text-center">
                {t('rep_partner_title')}
              </h3>
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
                {partners.map((partnerKey, index) => (
                  <div
                    key={index}
                    className="flex items-center justify-center p-4 rounded-lg bg-background-50 border border-background-200/70 text-sm text-foreground-700 font-medium text-center"
                  >
                    {t(partnerKey)}
                  </div>
                ))}
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-5 md:gap-6">
              {testimonials.map((item, index) => (
                <div key={index} className="p-5 md:p-6 rounded-lg bg-background-50 border border-background-200/70">
                  <div className="flex items-center gap-1 text-accent-500 mb-3">
                    {[...Array(5)].map((_, i) => (
                      <i key={i} className="ri-star-fill text-sm"></i>
                    ))}
                  </div>
                  <p className="text-sm text-foreground-700 leading-relaxed mb-4">
                    &ldquo;{t(item.textKey)}&rdquo;
                  </p>
                  <div>
                    <div className="text-sm font-medium text-foreground-950">{t(item.nameKey)}</div>
                    <div className="text-xs text-foreground-500">{t(item.titleKey)}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="w-full px-4 md:px-6 py-10 md:py-16 bg-background-50">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-10 md:mb-14">
              <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-950 mb-3">
                {t('section_contact')}
              </h2>
              <p className="text-sm md:text-base text-foreground-600 max-w-xl mx-auto">
                {t('section_contact_desc')}
              </p>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 md:gap-12">
              <div className="flex flex-col gap-5">
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-primary-100 text-primary-600 shrink-0">
                    <i className="ri-map-pin-line text-lg"></i>
                  </div>
                  <div>
                    <div className="text-sm font-medium text-foreground-950 mb-1">{t('contact_address_label')}</div>
                    <div className="text-sm text-foreground-600">{t('contact_address')}</div>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-accent-100 text-accent-600 shrink-0">
                    <i className="ri-phone-line text-lg"></i>
                  </div>
                  <div>
                    <div className="text-sm font-medium text-foreground-950 mb-1">{t('contact_phone_label')}</div>
                    <div className="text-sm text-foreground-600">{t('contact_phone')}</div>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-secondary-100 text-secondary-700 shrink-0">
                    <i className="ri-mail-line text-lg"></i>
                  </div>
                  <div>
                    <div className="text-sm font-medium text-foreground-950 mb-1">{t('contact_email_label')}</div>
                    <div className="text-sm text-foreground-600">{t('contact_email')}</div>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-primary-100 text-primary-600 shrink-0">
                    <i className="ri-wechat-line text-lg"></i>
                  </div>
                  <div>
                    <div className="text-sm font-medium text-foreground-950 mb-1">{t('contact_wechat_label')}</div>
                    <div className="text-sm text-foreground-600">{t('contact_wechat')}</div>
                  </div>
                </div>
              </div>

              <div className="p-5 md:p-6 rounded-lg bg-background-100 border border-background-200/70">
                <form onSubmit={handleSubmit} data-readdy-form="true" className="flex flex-col gap-4">
                  <div>
                    <label htmlFor="contact-name" className="block text-sm font-medium text-foreground-800 mb-1.5">
                      {t('contact_form_name')}
                    </label>
                    <input
                      id="contact-name"
                      name="name"
                      type="text"
                      required
                      placeholder={t('contact_form_name_placeholder')}
                      className="w-full px-3.5 py-2.5 rounded-md border border-background-300 bg-background-50 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-300 focus:ring-1 focus:ring-primary-200 transition-colors"
                    />
                  </div>

                  <div>
                    <label htmlFor="contact-email" className="block text-sm font-medium text-foreground-800 mb-1.5">
                      {t('contact_form_email')}
                    </label>
                    <input
                      id="contact-email"
                      name="email"
                      type="email"
                      required
                      placeholder={t('contact_form_email_placeholder')}
                      className="w-full px-3.5 py-2.5 rounded-md border border-background-300 bg-background-50 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-300 focus:ring-1 focus:ring-primary-200 transition-colors"
                    />
                  </div>

                  <div>
                    <label htmlFor="contact-subject" className="block text-sm font-medium text-foreground-800 mb-1.5">
                      {t('contact_form_subject')}
                    </label>
                    <select
                      id="contact-subject"
                      name="subject"
                      required
                      className="w-full px-3.5 py-2.5 rounded-md border border-background-300 bg-background-50 text-sm text-foreground-900 focus:outline-none focus:border-primary-300 focus:ring-1 focus:ring-primary-200 transition-colors cursor-pointer"
                    >
                      <option value="">{t('contact_form_subject_placeholder')}</option>
                      <option value="course">{t('subject_option_1')}</option>
                      <option value="cooperation">{t('subject_option_2')}</option>
                      <option value="expert">{t('subject_option_3')}</option>
                      <option value="institution">{t('subject_option_4')}</option>
                      <option value="other">{t('subject_option_5')}</option>
                    </select>
                  </div>

                  <div>
                    <label htmlFor="contact-message" className="block text-sm font-medium text-foreground-800 mb-1.5">
                      {t('contact_form_message')}
                    </label>
                    <textarea
                      id="contact-message"
                      name="message"
                      required
                      maxLength={500}
                      rows={4}
                      placeholder={t('contact_form_message_placeholder')}
                      className="w-full px-3.5 py-2.5 rounded-md border border-background-300 bg-background-50 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-300 focus:ring-1 focus:ring-primary-200 transition-colors resize-none"
                    ></textarea>
                    <p className="text-xs text-foreground-500 mt-1">最多500字</p>
                  </div>

                  <input
                    type="text"
                    name="company_alt"
                    tabIndex={-1}
                    autoComplete="off"
                    aria-hidden="true"
                    readOnly
                    className="honeypot-field"
                  />

                  <button
                    type="submit"
                    disabled={submitting}
                    className="w-full py-2.5 rounded-md bg-primary-500 text-background-50 text-sm font-medium hover:bg-primary-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-200 cursor-pointer whitespace-nowrap"
                  >
                    {submitting ? '发送中...' : t('contact_form_submit')}
                  </button>

                  {formStatus === 'success' && (
                    <div className="flex items-center gap-2 text-sm text-secondary-700 bg-secondary-100/80 px-4 py-2.5 rounded-md">
                      <i className="ri-check-line"></i>
                      {t('contact_form_success')}
                    </div>
                  )}
                  {formStatus === 'error' && (
                    <div className="flex items-center gap-2 text-sm text-accent-700 bg-accent-100/80 px-4 py-2.5 rounded-md">
                      <i className="ri-error-warning-line"></i>
                      {formError || t('contact_form_error')}
                    </div>
                  )}
                </form>
              </div>
            </div>
          </div>
        </section>
      </main>
    </>
  );
}